import axios from 'axios';
import { format } from 'date-fns';
import download from 'downloadjs';
import qs from 'qs';

export type JobReportFilter = {
  states?: string[];
  status?: string[];
  clients?: string[];
  projectCoordinators?: string[];
  supervisors?: string[];
  created?: { from?: string; to?: string };
  completed?: { from?: string; to?: string };
};

export type ShiftSummaryReportFilter = {
  states?: string[];
  users?: string[];
  clients?: string[];
  dateRange?: { from?: string; to?: string };
};

export type TimesheetSummaryReportFilter = {
  states?: string[];
  users?: string[];
  clients?: string[];
  dateRange?: { from?: string; to?: string };
};

export type MarginSummaryCategory =
  | 'labour'
  | 'equipment'
  | 'consumables'
  | 'otherExpenses';

export type CombinationType =
  | 'default'
  | 'branchSpecific'
  | 'clientSpecific'
  | 'branchAndClient';

export type ComparisonBasisLabour = 'roleDefault' | 'notFound';

export type ComparisonBasisBuyRates =
  | 'branchMatch'
  | 'defaultRate'
  | 'notFound';

export type ComparisonBasisOtherExpenses = 'configuredMargin';

export type ComparisonBasis =
  | ComparisonBasisLabour
  | ComparisonBasisBuyRates
  | ComparisonBasisOtherExpenses;

export interface MarginSummaryDetail {
  category: MarginSummaryCategory;
  name: string;
  combinationType: CombinationType;
  sellRate: number | null;
  buyRate: number | null;
  margin: number | null;
  branch?: string;
  client?: string;
  comparisonBasis: ComparisonBasis;
}

export interface AverageMargin {
  category: MarginSummaryCategory;
  overallAverage: number | null;
  breakdown: {
    combinationType: CombinationType;
    averageMargin: number | null;
    count: number;
  }[];
  details: MarginSummaryDetail[];
}

export async function getMarginSummary(): Promise<AverageMargin[]> {
  const res = await axios.get('/api/v1/management-reports/margin-summary');
  return res.data;
}

export async function generateJobReport({
  filters,
}: {
  filters?: JobReportFilter;
}) {
  const res = await axios.get(
    '/api/v1/management-reports/generate-job-report',
    {
      responseType: 'arraybuffer',
      params: filters,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
    }
  );
  const mime = res.headers['Content-Type'];
  download(
    res.data,
    `job-report-${format(new Date(), 'EEEE, dd MMM yyyy')}.xlsx`,
    mime
  );
}

export async function generateShiftSummaryReport({
  filters,
}: {
  filters?: ShiftSummaryReportFilter;
}) {
  const res = await axios.get(
    '/api/v1/management-reports/generate-shift-summary-report',
    {
      responseType: 'arraybuffer',
      params: filters,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
    }
  );
  const mime = res.headers['Content-Type'];
  download(
    res.data,
    `shift-summary-report-${format(new Date(), 'EEEE, dd MMM yyyy')}.xlsx`,
    mime
  );
}

export async function generateTimesheetSummaryReport({
  filters,
}: {
  filters?: TimesheetSummaryReportFilter;
}) {
  const res = await axios.get(
    '/api/v1/management-reports/generate-timesheet-summary-report',
    {
      responseType: 'arraybuffer',
      params: filters,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
    }
  );
  const mime = res.headers['Content-Type'];
  download(
    res.data,
    `timesheet-summary-report-${format(new Date(), 'EEEE, dd MMM yyyy')}.xlsx`,
    mime
  );
}
